/* Custom variant picker: groups size options into shape tabs with a collapsible list.
   Moved out of snippets/variant-picker.liquid during the 10.3 -> 11.4 upgrade so it is
   registered once per page instead of once per rendered picker. */

(function(){
  const ADAPTIVE_THRESHOLD = 40;            // Show the toggle only if a tab has > 40 options
  const state = (window.__sizeTabsState ||= {}); // per-form tab memory

  // Map picker -> form key
  function formKeyFor(picker){
    return picker?.closest('variant-picker')?.getAttribute('form-id') || 'default';
  }

  /* Locate the option card that holds the real selection.
   *
   * The "All" tab is built with cloneNode(), and the clone of the selected card
   * gets a copied checked="checked" attribute so it looks selected there. That
   * made a plain picker.querySelector('input[checked]') match the CLONE, because
   * the All group is rendered ahead of the shape groups — so `card` resolved into
   * .group-all while the visible group was .group-rectangular, and run() fell
   * through to the "selection is in another tab" return without ever scrolling.
   * Nothing logged, nothing moved.
   *
   * So skip clones here. The All tab is still handled, deliberately, further down
   * by looking the clone up from the real input's id. */
  function realSelectedCard(picker) {
    const radios = picker.querySelectorAll('input[type="radio"]:checked, input[type="radio"][checked]');
    for (const radio of radios) {
      const card = radio.closest(".custom-variant-option-wrapper, .popover__value-option, label");
      if (!card || card.hasAttribute("data-real-id")) continue; // clone living in the All tab
      return { radio, card };
    }
    return null;
  }

  // Scroll to selected within current/target tab; optionally switch tab (autoMode)
  function scrollSelectedWithRetries(picker, { attempts = 8, smooth = true, autoMode = false } = {}) {
    if (!picker) return;

    const run = (left) => {
      const retry = () => { if (left > 0) requestAnimationFrame(() => run(left - 1)); };

      const found = realSelectedCard(picker);
      if (!found) return retry();

      const targetGroup = found.card.closest(".variant-group");
      if (!targetGroup) return retry();

      const tabs = picker.parentElement.querySelector(".variant-tabs");
      const thisGroupKey = Array.from(targetGroup.classList).find(c => c.startsWith("group-"))?.replace("group-", "");

      // When autoMode=true (first load / quick buy), activate the tab that holds the selection
      if (autoMode && thisGroupKey) {
        activateTab(picker, tabs, thisGroupKey);
        state[formKeyFor(picker)] = { active: thisGroupKey };
      }

      // When autoMode=false, respect the tab the visitor is on
      const visibleGroup = picker.querySelector(".variant-group.visible");
      if (!visibleGroup) return retry();

      let card = found.card;

      if (!visibleGroup.contains(card)) {
        // Selection sits in a different shape tab and we do not auto-switch here
        if (!visibleGroup.classList.contains("group-all")) return;

        const clone = visibleGroup.querySelector(`[data-real-id="${CSS.escape(found.radio.id)}"]`);
        if (!clone) return retry();
        card = clone;
      }

      const inner = visibleGroup.querySelector(".size-collapsible");

      if (inner) {
        const targetTop = card.offsetTop - Math.max(0, (inner.clientHeight - card.clientHeight) / 2);
        inner.scrollTo({ top: Math.max(0, targetTop), behavior: smooth ? "smooth" : "auto" });
      } else {
        card.scrollIntoView({ block: "nearest", inline: "nearest", behavior: smooth ? "smooth" : "auto" });
      }
    };

    requestAnimationFrame(() => run(attempts));
  }

  function activateTab(picker, tabs, key){
    tabs?.querySelectorAll(".variant-tab").forEach(btn=>{
      const on = btn.dataset.group===key;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    });
    picker.querySelectorAll(".variant-group").forEach(g=>{
      g.classList.toggle("visible", g.classList.contains(`group-${key}`));
    });
  }

  function setupSizeTabs(scope=document, { autoMode = false } = {}){
    const picker = scope.querySelector(".variant-picker__option-values[data-option-name='size']");
    if (!picker || picker.dataset.tabsInitialized === "true") return;

    /* Collect one entry per option.
     *
     * The three selectors below overlap: an option card matches
     * .custom-variant-option-wrapper, and the label inside it separately
     * matches .popover__value-option (it carries both classes). Both then
     * resolve back to the same wrapper via closest(), so without the dedupe
     * every option is collected twice.
     *
     * That went unnoticed in the shape tabs because appending the same node
     * twice just moves it — a node can only live in one place. The "All" tab
     * uses cloneNode(), so two entries became two visible cards: 182 cards for
     * 91 sizes. Keep the Set. */
    const seenBlocks = new Set();
    const optionBlocks = Array.from(
      picker.querySelectorAll(".custom-variant-option-wrapper, .popover__value-option, label input[type='radio']")
    ).map(el =>
      el.closest(".custom-variant-option-wrapper") ||
      el.closest(".popover__value-option") ||
      el.closest("label") || el
    ).filter(el => {
      if (!el || seenBlocks.has(el)) return false;
      seenBlocks.add(el);
      return true;
    });

    if (!optionBlocks.length) return;
    picker.dataset.tabsInitialized = "true";

    // helpers
    const extractDimensions = (label) => {
      const n = (label||"").toLowerCase()
        .replace(/″|”|“/g,'"').replace(/′|’|‘/g,"'")
        .replace(/feet|foot|ft|inches|inch|in/g,"").replace(/\s+/g,'');
      const m = n.match(/([0-9.'"/]+)[x×]([0-9.'"/]+)/i);
      if (!m) return [0,0];
      const toDec = (s) => {
        s = s.trim();
        if (s.includes("'")) {
          const [f,i] = s.split("'").map(x=>x.replace('"','').trim());
          return (parseFloat(f)||0) + (parseFloat(i)||0)/12;
        }
        if (s.includes("/")) {
          return s.split(" ").reduce((sum, part)=>{
            if (part.includes("/")) { const [a,b]=part.split("/"); return sum + ((parseFloat(a)||0)/(parseFloat(b)||1)); }
            return sum + (parseFloat(part)||0);
          },0);
        }
        return parseFloat(s.replace(/[^0-9.]/g,""))||0;
      };
      return [toDec(m[1]), toDec(m[2])];
    };
    const parseSize = (label) => {
      const c = (label||"").toLowerCase();
      if (c.includes("round")) return "Round";
      if (c.includes("square")) return "Square";
      if (c.includes("runner")) return "Runner";
      const [w,l] = extractDimensions(label||"");
      if (w && l && l/w >= 2.5) return "Runner";
      return "Rectangular";
    };

    // build groups
    const groups = { Rectangular: [], Runner: [], Square: [], Round: [] };
    const allClones = [];
    let detectedGroup = null;

    optionBlocks.forEach(block=>{
      const labelText = (block.textContent||"").trim();
      const group = parseSize(labelText);
      groups[group]?.push(block);

      const realInput = block.querySelector('input[type="radio"]');
      if (realInput?.checked) detectedGroup = group.toLowerCase();

      // Clone for ALL with data-real-id to match
      const clone = block.cloneNode(true);
      if (realInput) {
        clone.setAttribute('data-real-id', realInput.id || '');
        // Mirror selected state for visual consistency
        if (realInput.checked) {
          clone.querySelector('input[type="radio"]')?.setAttribute('checked','checked');
          clone.querySelector('.custom-variant-option-label')?.classList.add('selected-from-original');
        }
      }
      allClones.push(clone);
    });

    const tabOrder = ["All","Rectangular","Runner","Square","Round"];
    const shapeGroups = Object.keys(groups).filter(k=>groups[k].length>0);
    const multiple = shapeGroups.length>1;

    // tabs
    const tabs = document.createElement("nav");
    tabs.className = "variant-tabs";
    tabs.setAttribute("role","tablist");
    tabs.setAttribute("aria-label","Variant Shape Tabs");

    tabOrder.forEach(g=>{
      const isAll = g==="All";
      const show  = (!isAll && groups[g]?.length) || (isAll && multiple);
      if (!show) return;
      const btn = document.createElement("button");
      btn.className = "variant-tab";
      btn.type="button";
      btn.dataset.group = g.toLowerCase();
      btn.setAttribute("role","tab");
      btn.setAttribute("aria-selected","false");
      btn.textContent = g;
      tabs.appendChild(btn);
    });

    // groups container
    const groupsWrap = document.createElement("div");
    groupsWrap.className = "variant-groups";

    const makeGroup = (key, blocksArr) => {
      const groupDiv = document.createElement("div");
      groupDiv.className = `variant-group group-${key}`;

      const inner = document.createElement("div");
      inner.className = "size-collapsible";
      blocksArr.forEach(b => inner.appendChild(b));

      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "variant-collapse-toggle";
      toggle.setAttribute("aria-expanded","false");
      toggle.textContent = "Show more options";

      if (blocksArr.length > ADAPTIVE_THRESHOLD) {
        toggle.hidden = false;
        toggle.addEventListener("click", ()=>{
          const expanded = inner.classList.toggle("is-expanded");
          toggle.setAttribute("aria-expanded", expanded ? "true":"false");
          toggle.textContent = expanded ? "Show fewer options" : "Show more options";
          if (!expanded) {
            scrollSelectedWithRetries(picker, { attempts: 2, smooth: true, autoMode: false });
          }
        });
      } else {
        toggle.hidden = true; // free-scroll only
      }

      groupDiv.appendChild(inner);
      groupDiv.appendChild(toggle);
      return groupDiv;
    };

    tabOrder.forEach(g=>{
      const arr = g==="All" ? allClones : (groups[g]||[]);
      if (!arr.length) return;
      groupsWrap.appendChild(makeGroup(g.toLowerCase(), arr));
    });

    // render
    picker.innerHTML = "";
    if (multiple) picker.parentNode.insertBefore(tabs, picker);
    picker.appendChild(groupsWrap);

    // Pick initial visible tab:
    const fk = formKeyFor(picker);
    const remembered = state[fk]?.active;
    const initialKey = (autoMode
                        ? (detectedGroup || remembered || (multiple ? "all" : (shapeGroups[0]?.toLowerCase()||"rectangular")))
                        : (remembered || detectedGroup || (multiple ? "all" : (shapeGroups[0]?.toLowerCase()||"rectangular"))));

    activateTab(picker, tabs, initialKey);
    // mark active on button
    const activeBtn = tabs.querySelector(`.variant-tab[data-group="${CSS.escape(initialKey)}"]`);
    if (activeBtn) { activeBtn.classList.add("active"); activeBtn.setAttribute("aria-selected","true"); }

    // Scroll after build
    scrollSelectedWithRetries(picker, { attempts: 8, smooth: true, autoMode });

    // Tab click -> remember active tab + scroll within it
    tabs?.addEventListener("click",(e)=>{
      const btn = e.target.closest(".variant-tab");
      if (!btn) return;
      const key = btn.dataset.group;
      state[fk] = { active: key };
      activateTab(picker, tabs, key);
      tabs.querySelectorAll(".variant-tab").forEach(b=>{
        const on = b === btn;
        b.classList.toggle("active", on);
        b.setAttribute("aria-selected", on ? "true" : "false");
      });
      scrollSelectedWithRetries(picker, { attempts: 3, smooth: true, autoMode: false });
    });
  }

  // First load: auto open the correct tab
  document.addEventListener("DOMContentLoaded", () => {
    setupSizeTabs(document, { autoMode: true });
  });

  // On product re-render (variant change): respect current tab
  document.addEventListener("product:rerender", () => {
    queueMicrotask(() => {
      document.querySelectorAll(".variant-picker__option-values[data-option-name='size']")
        .forEach(p => p.dataset.tabsInitialized = "false");
      setupSizeTabs(document, { autoMode: false });
    });
  });

  // Quick Buy: treat as first load inside the modal
  document.addEventListener("dialog:after-show", (e)=>{
    if (e.target.tagName === "QUICK-BUY-MODAL") {
      e.target.querySelectorAll(".variant-picker__option-values[data-option-name='size']")
        .forEach(p => p.dataset.tabsInitialized = "false");
      setupSizeTabs(e.target, { autoMode: true });
    }
  });

  // Safety net if something swaps DOM unexpectedly
  const mo = new MutationObserver(()=>{
    const p = document.querySelector(".variant-picker__option-values[data-option-name='size']");
    if (p && p.dataset.tabsInitialized !== "true") {
      // prefer remembered tab if we have it
      setupSizeTabs(document, { autoMode: false });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
